import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { getUserCheckInHistory } from "../../services/checkInService";
import { CheckInEventData } from "../../types/CheckInEvent";
import { formatDate } from "../../utils/format-date";
import ErrorDisplay from "../ErrorDisplay/ErrorDisplay";
import { Headings } from "../Headings";
import LoadingMask from "../LoadingMask/LoadingMask";
import "./UserCheckInHistory.scss";

interface UserCheckInHistoryProps {
  userId: string;
}

const UserCheckInHistory: React.FC<UserCheckInHistoryProps> = ({ userId }) => {
  const [checkIns, setCheckIns] = useState<CheckInEventData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchHistory = async () => {
      if (!userId) {
        setLoading(false);
        setCheckIns([]);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const history = await getUserCheckInHistory(userId);
        setCheckIns(history);
      } catch (err) {
        console.error("Error fetching user check-in history:", err);
        setError("Failed to load check-in history.");
      } finally {
        setLoading(false);
      }
    };

    fetchHistory();
  }, [userId]);

  const checkinLinks = useMemo(() => {
    const placeIds: Record<string, boolean> = {};
    const links: React.ReactNode[] = [];
    checkIns.forEach((checkIn) => {
      if (!placeIds[checkIn.placeId]) {
        placeIds[checkIn.placeId] = true;
        links.push(
          <div key={checkIn.placeId}>
            <Link to={`/place/${checkIn.placeId}`}>{checkIn.placeName}</Link>
            <div className="checkin-time">
              {formatDate(checkIn.checkInTime.toDate())}
            </div>
          </div>
        );
      }
    });

    return links;
  }, [checkIns]);

  if (loading) {
    return <LoadingMask message="Loading history..." />;
  }

  return (
    <div className="user-check-in-history">
      <Headings.PhoneSubBox>Check-in History</Headings.PhoneSubBox>
      {error && <ErrorDisplay error={error} />}
      {checkinLinks.length === 0 ? (
        <div className="alert alert-warning">
          You haven't checked in to any places yet.
        </div>
      ) : (
        checkinLinks
      )}
    </div>
  );
};

export default UserCheckInHistory;
